import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { inviteRoleLabel, useAcceptInvite, usePendingInvites } from '@/_shared/hooks/usePendingInvites';
import { cn } from '@/_shared/lib/utils';

/**
 * The signed-in person's live invites, with an accept button each (ACT2-1124). The workspace pickers show it, so a
 * person who already has a workspace, or who was signed in when the invite came, still sees the invite. Nothing
 * renders while the list loads or when it is empty.
 */
export function PendingInvites({ className }: { className?: string }): React.ReactElement | null {
  const invitesQuery = usePendingInvites();
  const accept = useAcceptInvite();

  if (invitesQuery.isPending) return null;
  // A failed background reload keeps the cards of the last good list: the pickers hide the Join cards from it too.
  if (invitesQuery.isError && invitesQuery.data === undefined) {
    return (
      <Callout variant="warning" className={cn('text-left', className)}>
        Could not load your invites. Open the link in the invite email instead.
      </Callout>
    );
  }
  const { invites } = invitesQuery.data;
  if (invites.length === 0) return null;

  return (
    <section aria-labelledby="pending-invites-heading" className={cn('text-left', className)}>
      <h2
        id="pending-invites-heading"
        className="px-1 text-xs font-bold tracking-wider text-muted-foreground uppercase"
      >
        Your invites
      </h2>
      <p className="mt-1 mb-2 px-1 text-xs text-muted-foreground">An admin invited you to these workspaces.</p>
      <ul className="space-y-2.5">
        {invites.map((invite) => {
          const role = inviteRoleLabel(invite.role);
          return (
            <li key={invite.id} className="rounded-xl border border-border">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-3 px-4 py-3">
                <div className="flex max-w-full min-w-0 shrink-0 flex-col gap-1 text-left">
                  <TruncateWithTooltip className="text-sm font-medium text-foreground">
                    {invite.workspaceName}
                  </TruncateWithTooltip>
                  <p className="text-xs text-muted-foreground">Invited as {role.toLowerCase()}</p>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <Button
                    variant="primary"
                    size="md"
                    aria-label={`Accept the invite to ${invite.workspaceName} as ${role.toLowerCase()}`}
                    loading={accept.isPending && accept.variables === invite.id}
                    disabled={accept.isPending}
                    onClick={() => accept.mutate(invite.id)}
                  >
                    {`Accept invite as ${role}`}
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
